/**
 * LocaleSelector - Simple locale/language selector with flag icons
 * 
 * Used for selecting display language (e.g., in preview dialogs)
 * Shows flag icons from CDN and language names
 */

import * as React from 'react';
import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LANGUAGES, LanguageCode } from '@/data/languages';

// Common languages to show first
const COMMON_LOCALES: LanguageCode[] = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'ja', 'zh'];

// Get sorted locale list
const getSortedLocales = () => {
  const allCodes = Object.keys(LANGUAGES) as LanguageCode[];
  
  const common = allCodes
    .filter((code) => COMMON_LOCALES.includes(code))
    .sort((a, b) => COMMON_LOCALES.indexOf(a) - COMMON_LOCALES.indexOf(b));
  
  const others = allCodes
    .filter((code) => !COMMON_LOCALES.includes(code))
    .sort((a, b) => LANGUAGES[a].name.localeCompare(LANGUAGES[b].name));
  
  return [...common, ...others];
};

const SORTED_LOCALES = getSortedLocales();

interface LocaleSelectorProps {
  /** Current value (locale code like 'en', 'fr', or 'auto') */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Show "Auto" option (returns 'auto' value) */
  showAuto?: boolean;
  /** Auto option label */
  autoLabel?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Filter to only show specific locales */
  allowedLocales?: LanguageCode[];
  /** Additional class name */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'default' | 'sm';
}

export function LocaleSelector({
  value,
  onChange,
  showAuto = true,
  autoLabel = 'Auto (browser)',
  placeholder = 'Select language',
  allowedLocales,
  className,
  disabled,
  size = 'default',
}: LocaleSelectorProps) {
  const locales = allowedLocales 
    ? SORTED_LOCALES.filter((code) => allowedLocales.includes(code))
    : SORTED_LOCALES;

  const selectedLang = value && value !== 'auto' ? LANGUAGES[value as LanguageCode] : null;

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn(size === 'sm' && 'h-8 text-xs', className)}>
        <SelectValue placeholder={placeholder}>
          {value === 'auto' ? (
            <span className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{autoLabel}</span>
            </span>
          ) : selectedLang ? (
            <span className="flex items-center gap-2">
              <img
                src={`https://flagcdn.com/w20/${selectedLang.flagCode}.png`}
                alt={selectedLang.flagCode}
                className="h-3 w-4 shrink-0 object-cover"
              />
              <span>{selectedLang.name}</span>
            </span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {showAuto && (
          <SelectItem value="auto" className={cn(size === 'sm' && 'text-xs')}>
            <span className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{autoLabel}</span>
            </span>
          </SelectItem>
        )}
        {locales.map((code) => {
          const lang = LANGUAGES[code];
          if (!lang) return null;
          
          return (
            <SelectItem key={code} value={code} className={cn(size === 'sm' && 'text-xs')}>
              <span className="flex items-center gap-2">
                <img
                  src={`https://flagcdn.com/w20/${lang.flagCode}.png`}
                  alt={lang.flagCode}
                  className="h-3 w-4 shrink-0 object-cover"
                />
                <span>{lang.name}</span>
                <span className="text-muted-foreground text-[10px]">({code.toUpperCase()})</span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export default LocaleSelector;


